/**
 * Underwriting journal — compose notes into named sections with author/time
 * stamps and optional document attachments.
 */

import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Loader2, Paperclip, X } from 'lucide-react'
import { loansService } from '@/services/loans'
import { documentsService } from '@/services/documents'

export const NARRATIVE_SECTIONS: { key: string; label: string }[] = [
  { key: 'executive_summary', label: 'Executive Summary' },
  { key: 'deal_background', label: 'Deal Background' },
  { key: 'sponsor', label: 'Sponsor' },
  { key: 'market', label: 'Market' },
  { key: 'risks_and_mitigants', label: 'Risks & Mitigants' },
  { key: 'exit_strategy', label: 'Exit Strategy' },
  { key: 'general_call_notes', label: 'General Call Notes' },
  { key: 'pricing_feedback', label: 'Pricing Feedback' },
]

type NarrativeAttachment = { documentId: string; filename: string }

export type NarrativeNoteEntry = {
  id: string
  text: string
  author: string
  createdAt: string
  attachment?: NarrativeAttachment
}

type NarrativeSectionData = {
  text?: string
  updatedBy?: string
  updatedAt?: string
  entries?: NarrativeNoteEntry[]
}

function normalizeEntries(section?: NarrativeSectionData): NarrativeNoteEntry[] {
  if (!section) return []
  if (section.entries?.length) {
    return [...section.entries].sort((a, b) => {
      const ta = a.createdAt ? Date.parse(a.createdAt) : 0
      const tb = b.createdAt ? Date.parse(b.createdAt) : 0
      return tb - ta
    })
  }
  if (section.text?.trim()) {
    return [
      {
        id: 'legacy',
        text: section.text,
        author: section.updatedBy ?? 'unknown',
        createdAt: section.updatedAt ?? '',
      },
    ]
  }
  return []
}

/**
 * A section's notes by its vocabulary key. The API keys sections in
 * snake_case (`deal_background`), but `loansService.getById` camelises the
 * whole payload, so the same section arrives as `dealBackground`. Reading only
 * the snake key hid every multi-word section's notes.
 */
function narrativeSection(
  narratives: Record<string, NarrativeSectionData>,
  key: string,
): NarrativeSectionData | undefined {
  const camel = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  return narratives[key] ?? narratives[camel]
}

async function downloadAttachment(documentId: string, filename: string) {
  const blob = await documentsService.download(documentId)
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function NarrativesPanel({
  dealId,
  readOnly = false,
}: {
  dealId: string
  readOnly?: boolean
}) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data: loan, isLoading, isError } = useQuery({
    queryKey: ['loan', dealId],
    queryFn: () => loansService.getById(dealId),
    retry: false,
  })

  const [section, setSection] = useState(NARRATIVE_SECTIONS[0].key)
  const [draft, setDraft] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-[12px] text-stone-500">
        <Loader2 size={14} className="animate-spin" /> Loading journal…
      </div>
    )
  }

  if (isError || !loan) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <FileText size={32} className="mx-auto mb-3 text-stone-600" />
          <p className="text-sm text-stone-400">
            Underwriting narratives persist on live deals — this record is not a live loan.
          </p>
        </div>
      </div>
    )
  }

  const narratives =
    ((loan as unknown as { narratives?: Record<string, NarrativeSectionData> }).narratives) ?? {}

  const save = async () => {
    const text = draft.trim()
    if (!text || readOnly) return
    setSaving(true)
    setError(null)
    try {
      let attachmentRef: { documentId: string; filename: string } | undefined
      if (attachment) {
        const doc = await documentsService.upload(dealId, attachment, {
          category: 'narrative_attachment',
        })
        attachmentRef = { documentId: doc.id, filename: doc.filename }
      }
      await loansService.appendNarrativeEntry(dealId, {
        section,
        text,
        attachment: attachmentRef,
      })
      setDraft('')
      setAttachment(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setSavedAt(new Date().toLocaleTimeString())
      await queryClient.invalidateQueries({ queryKey: ['loan', dealId] })
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Could not save note. Please retry.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="shrink-0 rounded-lg border border-stone-800 bg-black/30 p-2.5">
        <h2 className="mb-2 text-[10px] font-mono uppercase tracking-widest text-stone-500">
          Journal Entries
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={section}
            onChange={(e) => setSection(e.target.value)}
            disabled={readOnly || saving}
            aria-label="Section"
            className="h-9 shrink-0 rounded border border-stone-700 bg-[#0d0d0d] px-2 text-[11px] text-stone-200 disabled:opacity-50"
          >
            {NARRATIVE_SECTIONS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <div className="min-w-0 flex-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              readOnly={readOnly}
              rows={1}
              placeholder={
                readOnly
                  ? 'Unlock editing in the header to add notes'
                  : 'Write a note, pick the section it belongs to, and save…'
              }
              className="h-9 w-full resize-none rounded border border-stone-700 bg-[#0d0d0d] px-2.5 py-2 text-[12px] leading-tight text-stone-200 placeholder:text-stone-600 focus:border-amber-400/40 focus:outline-none disabled:opacity-50"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            disabled={readOnly || saving}
            onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            disabled={readOnly || saving}
            onClick={() => fileInputRef.current?.click()}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-stone-700 text-stone-400 hover:border-stone-600 hover:text-stone-200 disabled:opacity-40"
            title="Attach document"
            aria-label="Attach document"
          >
            <Paperclip size={14} />
          </button>
          <button
            type="button"
            onClick={save}
            disabled={readOnly || saving || !draft.trim()}
            className="btn-primary flex h-9 shrink-0 items-center px-4 text-[11px] disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save note'}
          </button>
        </div>
        {attachment && (
          <div className="mt-2 flex items-center gap-2 text-[10px] text-stone-400">
            <Paperclip size={12} />
            <span className="truncate">{attachment.name}</span>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setAttachment(null)
                if (fileInputRef.current) fileInputRef.current.value = ''
              }}
              className="text-stone-500 hover:text-stone-300"
            >
              <X size={12} />
            </button>
          </div>
        )}
        <div className="mt-1.5 flex items-center gap-2">
          {savedAt && (
            <span className="text-[10px] text-emerald-400">Saved {savedAt}</span>
          )}
          {error && <span className="text-[10px] text-red-300">{error}</span>}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {NARRATIVE_SECTIONS.map((s) => {
            const entries = normalizeEntries(narrativeSection(narratives, s.key))
            return (
              <section
                key={s.key}
                className="rounded-lg border border-stone-800/80 bg-black/20 px-2.5 py-2"
              >
                <div className="mb-1.5 flex items-baseline justify-between gap-2">
                  <h3 className="text-[11px] font-medium text-stone-200">{s.label}</h3>
                  <span className="shrink-0 text-[9px] text-stone-600">
                    {entries.length} note{entries.length === 1 ? '' : 's'}
                  </span>
                </div>
                {entries.length === 0 ? (
                  <p className="text-[10px] text-stone-600">No notes yet.</p>
                ) : (
                  <ul className="max-h-40 space-y-1.5 overflow-y-auto pr-0.5">
                    {entries.map((entry) => (
                      <li
                        key={entry.id}
                        className="rounded border border-stone-800/60 bg-[#0a0a0a] px-2 py-1.5"
                      >
                        <div className="mb-0.5 text-[9px] text-stone-600">
                          {entry.author}
                          {entry.createdAt
                            ? ` · ${new Date(entry.createdAt).toLocaleString()}`
                            : ''}
                        </div>
                        <div className="whitespace-pre-wrap text-[11px] leading-snug text-stone-300">
                          {entry.text}
                        </div>
                        {entry.attachment && (
                          <button
                            type="button"
                            onClick={() =>
                              downloadAttachment(
                                entry.attachment!.documentId,
                                entry.attachment!.filename,
                              )
                            }
                            className="mt-1 flex items-center gap-1 text-[10px] text-amber-300/90 hover:text-amber-200"
                          >
                            <Paperclip size={10} />
                            {entry.attachment.filename}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default NarrativesPanel
